import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { generateClient } from 'aws-amplify/api';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Typography, Stack, Button, Chip,
  Alert, Skeleton, useTheme, useMediaQuery,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import Seo, { stayJsonLd } from '../../components/Common/Seo';
import Reveal from '../../components/FarmStay/Reveal';
import StickyBookBar from '../../components/FarmStay/StickyBookBar';
import { getStayPackageBySlug, listFarmPhotos } from '../../graphql/farmStayQueries';
import { quoteStay, formatINR, ageBands } from '../../utils/farmStayPricing';
import { policyOf, dayVisitTiers, HOUSE_NOTES, CANCELLATION_TIERS } from '../../utils/farmStayPolicy';
import { surface, brand, ink, line, type } from '../../theme/tokens';

const client = generateClient();

export const PACKAGE_SLUG = 'weekend-in-birbhum';

const byOrder = (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0);

/** "Day 2 — Saturday" -> "Saturday". Falls back to the whole title. */
const weekdayOf = (day) => {
  const t = day?.title || '';
  const after = t.split(/[—–-]/).pop().trim();
  return after || t || `Day ${day?.dayIndex ?? ''}`;
};

/** Display type. Fraunces carries the voice; Inter carries the reading. */
const DISPLAY = {
  fontFamily: type.display,
  fontWeight: 400,
  letterSpacing: '-0.02em',
  lineHeight: 1.08,
};

/** Small tracked label that opens each section — the editorial spine. */
const SectionMark = ({ index, children, onDark = false }) => (
  <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 2.5 }}>
    <Typography
      sx={{
        fontFamily: type.display, fontSize: 14, fontWeight: 500,
        color: onDark ? brand.mustard : brand.mustardInk,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {String(index).padStart(2, '0')}
    </Typography>
    <Box sx={{ flex: '0 0 40px', height: '1px', bgcolor: onDark ? 'rgba(242,241,236,0.35)' : line.default }} />
    <Typography
      variant="overline"
      sx={{
        letterSpacing: '0.18em', fontWeight: 700, fontSize: 11,
        color: onDark ? 'rgba(242,241,236,0.75)' : ink.secondary,
      }}
    >
      {children}
    </Typography>
  </Stack>
);

/** Hero: one photograph, one sentence, one price, one way in. No carousel. */
const Hero = ({ pkg, photo, isMobile }) => (
  <Box
    component="header"
    sx={{
      position: 'relative',
      minHeight: { xs: '86vh', md: '94vh' },
      display: 'flex',
      alignItems: 'flex-end',
      color: ink.onDark,
      bgcolor: surface.darker,
      backgroundImage: photo
        ? `linear-gradient(to top, rgba(45,32,30,0.92) 0%, rgba(45,32,30,0.55) 45%, rgba(45,32,30,0.38) 100%), url(${photo})`
        : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center 55%',
    }}
  >
    {/* a hairline grid, barely there — texture rather than decoration */}
    <Box
      aria-hidden
      sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage:
          'linear-gradient(rgba(242,241,236,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(242,241,236,0.05) 1px, transparent 1px)',
        backgroundSize: '96px 96px',
        maskImage: 'radial-gradient(ellipse at 50% 40%, black 20%, transparent 78%)',
      }}
    />
    <Container maxWidth="lg" sx={{ position: 'relative', pb: { xs: 7, md: 11 }, pt: { xs: 14, md: 18 } }}>
      <Reveal>
        <Typography
          variant="overline"
          sx={{ letterSpacing: '0.32em', fontSize: 11, fontWeight: 700, color: brand.mustard }}
        >
          Birbhum · West Bengal
        </Typography>
      </Reveal>

      <Reveal delay={90}>
        <Typography
          variant="h1"
          sx={{
            ...DISPLAY,
            fontSize: { xs: 46, sm: 68, md: 96, lg: 108 },
            mt: 1.5,
            maxWidth: 980,
            textShadow: '0 2px 40px rgba(0,0,0,0.35)',
          }}
        >
          {pkg?.name || 'A Weekend in Birbhum'}
        </Typography>
      </Reveal>

      <Reveal delay={180}>
        <Typography
          sx={{
            mt: 3, maxWidth: 560, fontSize: { xs: 16.5, md: 19 }, lineHeight: 1.65,
            color: 'rgba(242,241,236,0.88)',
          }}
        >
          {pkg?.summary
            || 'A slow countryside weekend of farm life, Bengali home cooking and Baul music, an hour from Shantiniketan.'}
        </Typography>
      </Reveal>

      <Reveal delay={260}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 3 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mt: { xs: 4, md: 6 } }}
        >
          <Button
            component={RouterLink}
            to="/farm-stay/book"
            size="large"
            endIcon={<ArrowForwardIcon />}
            sx={{
              bgcolor: brand.mustard, backgroundImage: 'none', color: ink.onMustard,
              fontWeight: 700, px: 4, py: 1.6, borderRadius: 0, fontSize: 16,
              '&:hover': { bgcolor: '#C2A20E', backgroundImage: 'none' },
            }}
          >
            Check dates
          </Button>
          <Box>
            <Typography sx={{ ...DISPLAY, fontSize: { xs: 28, md: 34 }, color: ink.onDark }}>
              {pkg?.price != null ? formatINR(pkg.price) : 'On request'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(242,241,236,0.72)', letterSpacing: '0.04em' }}>
              per person, per night · {pkg?.nights ?? 2} nights · {pkg?.minGuests ?? 2}–{pkg?.maxGuests ?? 4} guests
            </Typography>
          </Box>
        </Stack>
      </Reveal>
    </Container>
  </Box>
);

/** The argument for coming here rather than anywhere else. */
const Preamble = () => (
  <Container maxWidth="lg" sx={{ py: { xs: 9, md: 16 } }}>
    <Box sx={{ display: 'grid', gap: { xs: 4, md: 10 }, gridTemplateColumns: { xs: '1fr', md: '0.9fr 1.1fr' } }}>
      <Reveal>
        <SectionMark index={1}>Where you are</SectionMark>
        <Typography variant="h2" sx={{ ...DISPLAY, fontSize: { xs: 32, md: 46 }, color: ink.primary }}>
          Tagore’s district, an hour past the noise
        </Typography>
      </Reveal>
      <Reveal delay={120}>
        <Typography sx={{ fontSize: { xs: 16.5, md: 18.5 }, lineHeight: 1.8, color: ink.secondary }}>
          Vidhi is a working permaculture farm in Birbhum — the country around Shantiniketan, where
          Baul singers still walk between villages and Tantipara weavers still work the handloom.
          There is a fruit forest, a vegetable garden, a nursery, and a pond the ducks consider theirs.
        </Typography>
        <Typography sx={{ fontSize: { xs: 16.5, md: 18.5 }, lineHeight: 1.8, color: ink.secondary, mt: 2.5 }}>
          You get the whole place. Two to four of you, two nights, and nobody else’s schedule to keep.
        </Typography>
        {/* Someone deciding whether to come is moved by what the place was
            before it was this. The journey page is the strongest thing we have
            to show them, so link it from the section that sets the scene. */}
        <Button
          component={RouterLink}
          to="/about"
          endIcon={<ArrowForwardIcon />}
          sx={{
            mt: 3, px: 0, color: brand.mustardInk, fontWeight: 700,
            borderBottom: `2px solid ${brand.mustardInk}`, borderRadius: 0,
            '&:hover': { bgcolor: 'transparent', borderBottomColor: ink.primary, color: ink.primary },
          }}
        >
          See how it grew, 2020 to today
        </Button>
      </Reveal>
    </Box>
  </Container>
);

/** Three days as three acts, with the real itinerary underneath. */
const Days = ({ days, photos }) => (
  <Box sx={{ bgcolor: surface.card, borderTop: `1px solid ${line.subtle}`, borderBottom: `1px solid ${line.subtle}` }}>
    <Container maxWidth="lg" sx={{ py: { xs: 9, md: 16 } }}>
      <Reveal>
        <SectionMark index={2}>How it goes</SectionMark>
        <Typography variant="h2" sx={{ ...DISPLAY, fontSize: { xs: 32, md: 46 }, color: ink.primary, maxWidth: 700, mb: { xs: 5, md: 9 } }}>
          Three days, and nothing you have to be on time for
        </Typography>
      </Reveal>

      <Stack spacing={{ xs: 6, md: 12 }}>
        {days.map((day, i) => {
          const items = [...(day.items?.items || [])].sort(byOrder);
          const meals = items.filter((it) => it.kind === 'MEAL');
          const doings = items.filter((it) => it.kind !== 'MEAL');
          const photo = photos[i % Math.max(photos.length, 1)];
          const flip = i % 2 === 1;

          return (
            <Reveal key={day.id} delay={60}>
              <Box
                sx={{
                  display: 'grid', gap: { xs: 3, md: 7 }, alignItems: 'start',
                  gridTemplateColumns: { xs: '1fr', md: flip ? '1fr 0.85fr' : '0.85fr 1fr' },
                }}
              >
                <Box
                  sx={{
                    order: { xs: 1, md: flip ? 2 : 1 },
                    aspectRatio: { xs: '16 / 10', md: '4 / 5' },
                    bgcolor: surface.sunken,
                    backgroundImage: photo ? `url(${photo})` : 'none',
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    // the image runs past the column edge, so the grid never
                    // reads as two tidy boxes side by side
                    mr: { md: flip ? 0 : -6 }, ml: { md: flip ? -6 : 0 },
                  }}
                />
                <Box sx={{ order: { xs: 2, md: flip ? 1 : 2 } }}>
                  <Typography sx={{ ...DISPLAY, fontSize: 15, color: brand.mustardInk, mb: 1 }}>
                    Day {day.dayIndex}
                  </Typography>
                  {/* The heading is the weekday, not the subtitle. Subtitles are
                      stored uppercase because they were written as small tracked
                      labels ("EXPERIENCE THE FARM • EXPERIENCE BIRBHUM"); at 34px
                      in a display serif that reads as shouting. So: weekday large
                      and in sentence case, subtitle small and tracked, as intended. */}
                  <Typography variant="h3" sx={{ ...DISPLAY, fontSize: { xs: 26, md: 34 }, color: ink.primary }}>
                    {weekdayOf(day)}
                  </Typography>
                  {day.subtitle && (
                    <Typography
                      variant="overline"
                      sx={{ display: 'block', mt: 1, letterSpacing: '0.14em', fontSize: 10.5, fontWeight: 700, color: ink.muted, lineHeight: 1.6 }}
                    >
                      {day.subtitle}
                    </Typography>
                  )}

                  <Stack spacing={2.5} sx={{ mt: 3.5 }}>
                    {doings.slice(0, 4).map((it) => (
                      <Box key={it.id}>
                        <Typography sx={{ fontWeight: 600, color: ink.primary, fontSize: 16 }}>
                          {it.title}
                        </Typography>
                        {it.body && (
                          <Typography sx={{ color: ink.secondary, fontSize: 15, lineHeight: 1.7, mt: 0.25 }}>
                            {it.body}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>

                  {meals.length > 0 && (
                    <Box sx={{ mt: 3.5, pt: 3, borderTop: `1px solid ${line.subtle}` }}>
                      <Typography variant="overline" sx={{ letterSpacing: '0.16em', color: ink.muted, fontSize: 10.5, fontWeight: 700 }}>
                        At the table
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.25 }}>
                        {meals.map((m) => (
                          <Chip
                            key={m.id}
                            label={`${m.timeLabel ? `${m.timeLabel} · ` : ''}${m.title}`}
                            size="small"
                            sx={{
                              bgcolor: 'transparent', border: `1px solid ${line.default}`,
                              color: ink.secondary, borderRadius: 0, fontSize: 12.5,
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Box>
            </Reveal>
          );
        })}
      </Stack>
    </Container>
  </Box>
);

/** The food, framed as the thing it actually is: cooked to order, by hand. */
const Table = ({ pkg }) => (
  <Container maxWidth="lg" sx={{ py: { xs: 9, md: 16 } }}>
    <Box sx={{ display: 'grid', gap: { xs: 4, md: 10 }, gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' } }}>
      <Reveal>
        <SectionMark index={3}>The table</SectionMark>
        <Typography variant="h2" sx={{ ...DISPLAY, fontSize: { xs: 32, md: 46 }, color: ink.primary }}>
          Every meal is cooked to your answer, not ours
        </Typography>
        <Typography sx={{ fontSize: { xs: 16.5, md: 18.5 }, lineHeight: 1.8, color: ink.secondary, mt: 3 }}>
          Bengali home cooking, three meals a day, from the garden and the pond. You choose your
          dishes when you book — the bhaja, the curry, the evening snack, whether Saturday lunch is
          chicken, mutton or vegetarian — and the kitchen shops and cooks to that.
        </Typography>
        <Typography sx={{ fontSize: { xs: 16.5, md: 18.5 }, lineHeight: 1.8, color: ink.secondary, mt: 2.5 }}>
          If choosing is the last thing you want to do on a Friday, pick{' '}
          <Box component="span" sx={{ color: brand.mustardInk, fontWeight: 600 }}>Surprise us</Box>{' '}
          and let the kitchen decide what is best that day.
        </Typography>
      </Reveal>

      <Reveal delay={120}>
        <Box sx={{ borderLeft: { md: `1px solid ${line.default}` }, pl: { md: 6 } }}>
          {[
            ['Friday', 'Welcome lunch, tea and snacks by the pond, dinner after the bonfire'],
            ['Saturday', 'Luchi or kochuri, farm-to-table lunch, dinner under the stars'],
            ['Sunday', 'Morning tea and fruit, then the long Vidhi brunch before you go'],
          ].map(([day, what], i) => (
            <Box key={day} sx={{ py: 2.5, borderTop: i === 0 ? 'none' : `1px solid ${line.subtle}` }}>
              <Typography sx={{ ...DISPLAY, fontSize: 20, color: ink.primary }}>{day}</Typography>
              <Typography sx={{ color: ink.secondary, fontSize: 15.5, lineHeight: 1.7, mt: 0.5 }}>
                {what}
              </Typography>
            </Box>
          ))}
          {pkg?.mealChoiceCutoffDays != null && (
            <Typography variant="caption" sx={{ color: ink.muted, display: 'block', mt: 2 }}>
              Choices lock {pkg.mealChoiceCutoffDays} days before you arrive, so the kitchen can shop.
            </Typography>
          )}
        </Box>
      </Reveal>
    </Box>
  </Container>
);

/** The emotional peak of the weekend gets the darkest, quietest section. */
const BaulEvening = ({ photo }) => (
  <Box
    sx={{
      position: 'relative', color: ink.onDark, bgcolor: surface.darker,
      backgroundImage: photo
        ? `linear-gradient(rgba(45,32,30,0.82), rgba(45,32,30,0.9)), url(${photo})`
        : 'none',
      backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: { md: 'fixed' },
    }}
  >
    <Container maxWidth="md" sx={{ py: { xs: 11, md: 18 }, textAlign: 'center' }}>
      <Reveal>
        <SectionMark index={4} onDark>Saturday, after dark</SectionMark>
      </Reveal>
      <Reveal delay={100}>
        <Typography
          variant="h2"
          sx={{ ...DISPLAY, fontSize: { xs: 34, md: 56 }, mb: 3, mx: 'auto', maxWidth: 760 }}
        >
          A Baul singer, a bonfire, and no particular reason to go inside
        </Typography>
      </Reveal>
      <Reveal delay={180}>
        <Typography
          sx={{ fontSize: { xs: 16.5, md: 19 }, lineHeight: 1.8, color: 'rgba(242,241,236,0.86)', mx: 'auto', maxWidth: 620 }}
        >
          A local artist plays in the open, close enough that you can hear the ektara breathe between
          lines. Afterwards people usually stay out talking, which is the part nobody plans for and
          everybody remembers.
        </Typography>
      </Reveal>
    </Container>
  </Box>
);

/** What it costs, said plainly, with the arithmetic shown. */
const Cost = ({ pkg }) => {
  const n = pkg?.nights || 2;
  const tiers = dayVisitTiers(pkg);
  const bands = ageBands(pkg);
  const rows = [
    { label: 'Two of you', adults: pkg?.minGuests || 2, childAges: [] },
    { label: 'Four of you', adults: pkg?.maxGuests || 4, childAges: [] },
    // one of each child band, so the two rules are visible as money rather
    // than as a sentence someone has to apply to their own family
    { label: `Two, plus a child of ${bands.freeUpTo}`, adults: 2, childAges: [bands.freeUpTo] },
    { label: `Two, plus a child of ${bands.childTo}`, adults: 2, childAges: [bands.childTo] },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 9, md: 16 } }}>
      <Reveal>
        <SectionMark index={5}>What it costs</SectionMark>
        <Typography variant="h2" sx={{ ...DISPLAY, fontSize: { xs: 32, md: 46 }, color: ink.primary, maxWidth: 640, mb: 1.5 }}>
          One price, everything in it
        </Typography>
        <Typography sx={{ color: ink.secondary, fontSize: 17, maxWidth: 620, mb: { xs: 4, md: 7 } }}>
          {pkg?.price != null ? formatINR(pkg.price) : 'On request'} per person per night, for {n} nights.
          All meals, the Baul evening, the bonfire and the farm are included.
          {pkg?.childFreeUnderAge != null && ` Children ${bands.freeLabel} stay free`}
          {pkg?.childPrice != null && pkg?.childMaxAge != null
            && `, ${bands.childLabel} at ${formatINR(pkg.childPrice)} a night, and ${bands.adultFrom} and over at the adult rate`}.
        </Typography>
      </Reveal>

      <Reveal delay={100}>
        <Box sx={{ borderTop: `1px solid ${ink.primary}` }}>
          {rows.map((r) => {
            const q = quoteStay({ pkg, nights: n, adults: r.adults, childAges: r.childAges });
            return (
              <Stack
                key={r.label}
                direction="row"
                alignItems="baseline"
                justifyContent="space-between"
                sx={{ py: { xs: 2.5, md: 3 }, borderBottom: `1px solid ${line.subtle}`, gap: 2 }}
              >
                <Typography sx={{ fontSize: { xs: 16, md: 18 }, color: ink.primary }}>{r.label}</Typography>
                <Box sx={{ flex: 1, borderBottom: `1px dotted ${line.default}`, mx: 1, transform: 'translateY(-4px)' }} />
                <Typography sx={{ ...DISPLAY, fontSize: { xs: 22, md: 28 }, color: ink.primary, whiteSpace: 'nowrap' }}>
                  {formatINR(q.total)}
                </Typography>
              </Stack>
            );
          })}
        </Box>
        <Typography variant="caption" sx={{ color: ink.muted, display: 'block', mt: 2 }}>
          Nothing is charged online. You send the request, Ananya confirms the dates and the total on WhatsApp.
        </Typography>
      </Reveal>

      {tiers.length > 0 && (
        <Reveal delay={150}>
          <Box sx={{ mt: { xs: 6, md: 9 } }}>
            <Typography variant="h3" sx={{ ...DISPLAY, fontSize: { xs: 24, md: 30 }, color: ink.primary }}>
              Or just for the day
            </Typography>
            <Typography sx={{ color: ink.secondary, fontSize: 16, maxWidth: 620, mt: 1, mb: 3 }}>
              You do not have to stay the night. Same age rules as a stay — children
              {' '}{bands.freeLabel} come free and {bands.childLabel} pay half. A day visit is
              arranged for that one day, so the advance for it is not refundable.
            </Typography>
            <Box sx={{ borderTop: `1px solid ${ink.primary}` }}>
              {tiers.map((t) => (
                <Stack
                  key={t.id}
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'baseline' }}
                  justifyContent="space-between"
                  sx={{ py: { xs: 2, md: 2.5 }, borderBottom: `1px solid ${line.subtle}`, gap: { xs: 0.5, sm: 2 } }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: { xs: 16, md: 18 }, color: ink.primary }}>{t.label}</Typography>
                    {t.includes?.length > 0 && (
                      <Typography sx={{ color: ink.secondary, fontSize: 14, mt: 0.25 }}>
                        {t.includes.join(' · ')}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ ...DISPLAY, fontSize: { xs: 20, md: 26 }, color: ink.primary, whiteSpace: 'nowrap' }}>
                    {formatINR(t.price)}
                    <Typography component="span" sx={{ color: ink.secondary, fontSize: 13, ml: 0.5 }}>
                      per person
                    </Typography>
                  </Typography>
                </Stack>
              ))}
            </Box>
            {/* This section sold the day visit and then dead-ended: the next
                button down the page is the closing CTA, which is worded for a
                two-night stay. `?type=day` lands in the right product. */}
            <Button
              component={RouterLink}
              to="/farm-stay/book?type=day"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                mt: 3.5, bgcolor: brand.mustard, backgroundImage: 'none', color: ink.onMustard,
                fontWeight: 700, px: 4, py: 1.5, borderRadius: 0, fontSize: 15.5,
                '&:hover': { bgcolor: '#C2A20E', backgroundImage: 'none' },
              }}
            >
              Book a day visit
            </Button>
          </Box>
        </Reveal>
      )}
    </Container>
  );
};

/**
 * The answers to everything a guest asks over WhatsApp before booking: how the
 * farm works, when they have to decide, and what happens if they cannot come.
 * Sourced from utils/farmStayPolicy so the booking wizard quotes the same
 * numbers rather than a second set written into page copy.
 */
const Practicalities = ({ pkg }) => {
  const policy = policyOf(pkg);
  return (
    <Box sx={{ bgcolor: surface.sunken }}>
      <Container maxWidth="lg" sx={{ py: { xs: 9, md: 14 } }}>
        <Reveal>
          <SectionMark index={6}>Before you book</SectionMark>
          <Typography variant="h2" sx={{ ...DISPLAY, fontSize: { xs: 32, md: 46 }, color: ink.primary, maxWidth: 640, mb: { xs: 4, md: 7 } }}>
            How it all works
          </Typography>
        </Reveal>

        <Reveal delay={100}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: { xs: 3, md: 5 },
            }}
          >
            {HOUSE_NOTES.map((n) => (
              <Box key={n.id}>
                <Typography sx={{ ...DISPLAY, fontSize: { xs: 20, md: 23 }, color: ink.primary, mb: 0.75 }}>
                  {n.title}
                </Typography>
                <Typography sx={{ color: ink.secondary, fontSize: 15.5, lineHeight: 1.75 }}>
                  {n.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Reveal>

        <Reveal delay={150}>
          <Box
            sx={{
              mt: { xs: 5, md: 8 },
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: { xs: 3, md: 5 },
            }}
          >
            <Box>
              <Typography sx={{ ...DISPLAY, fontSize: { xs: 20, md: 23 }, color: ink.primary, mb: 1.25 }}>
                Holding your dates
              </Typography>
              <Stack spacing={1}>
                {[
                  [`${policy.depositPercent}% to confirm`, 'The rest is paid when you check out.'],
                  [`${policy.minNoticeDays} days' notice`, `And we take bookings up to ${policy.maxAdvanceMonths} months ahead.`],
                  [`Food chosen ${policy.mealChoiceCutoffDays} days ahead`, 'After that the kitchen has planned around it.'],
                ].map(([head, tail]) => (
                  <Box key={head}>
                    <Typography sx={{ color: ink.primary, fontWeight: 700, fontSize: 15 }}>{head}</Typography>
                    <Typography sx={{ color: ink.secondary, fontSize: 14.5, lineHeight: 1.6 }}>{tail}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography sx={{ ...DISPLAY, fontSize: { xs: 20, md: 23 }, color: ink.primary, mb: 1.25 }}>
                If you have to cancel
              </Typography>
              <Box sx={{ borderTop: `1px solid ${line.default}` }}>
                {CANCELLATION_TIERS.map((t) => (
                  <Stack
                    key={t.label}
                    direction="row"
                    alignItems="baseline"
                    justifyContent="space-between"
                    sx={{ py: 1.25, borderBottom: `1px solid ${line.subtle}`, gap: 2 }}
                  >
                    <Typography sx={{ color: ink.secondary, fontSize: 14.5 }}>{t.label}</Typography>
                    <Typography sx={{ color: ink.primary, fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap' }}>
                      {t.refundPercent}% back
                    </Typography>
                  </Stack>
                ))}
              </Box>
              <Typography sx={{ color: ink.muted, fontSize: 13.5, mt: 1.25, lineHeight: 1.6 }}>
                A day visit is put together for that one day, so its advance is not refundable.
              </Typography>
            </Box>
          </Box>
        </Reveal>
      </Container>
    </Box>
  );
};

/** The two things guests leave with. Small section, deliberately. */
const TakeHome = () => (
  <Box sx={{ bgcolor: surface.sunken }}>
    <Container maxWidth="lg" sx={{ py: { xs: 9, md: 14 } }}>
      <Reveal>
        <SectionMark index={7}>Going home</SectionMark>
      </Reveal>
      <Box sx={{ display: 'grid', gap: { xs: 4, md: 8 }, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        {[
          ['Plant something', 'Each family puts a seedling in the ground with a tag — your name, the date. Come back and see how tall it got.'],
          ['A gamcha off the loom', 'Every guest leaves with a handwoven gamcha from a Tantipara weaver down the road. Not a souvenir — a towel you will actually use.'],
        ].map(([title, body], i) => (
          <Reveal key={title} delay={i * 110}>
            <Typography variant="h3" sx={{ ...DISPLAY, fontSize: { xs: 25, md: 31 }, color: ink.primary }}>
              {title}
            </Typography>
            <Typography sx={{ color: ink.secondary, fontSize: 16.5, lineHeight: 1.8, mt: 1.5 }}>
              {body}
            </Typography>
          </Reveal>
        ))}
      </Box>
    </Container>
  </Box>
);

/** Close. */
const Closing = ({ pkg }) => (
  <Box sx={{ bgcolor: surface.dark, color: ink.onDark }}>
    <Container maxWidth="md" sx={{ py: { xs: 10, md: 16 }, textAlign: 'center' }}>
      <Reveal>
        <Typography variant="h2" sx={{ ...DISPLAY, fontSize: { xs: 32, md: 52 }, mb: 2.5 }}>
          Come for the farm. Stay for the experience.
        </Typography>
        <Typography sx={{ color: 'rgba(242,241,236,0.82)', fontSize: { xs: 16.5, md: 18.5 }, maxWidth: 540, mx: 'auto', lineHeight: 1.8 }}>
          {pkg?.minGuests ?? 2}–{pkg?.maxGuests ?? 4} guests, {pkg?.nights ?? 2} nights, the whole farm to yourselves.
          Pick your dates and tell us what you would like to eat.
        </Typography>
        <Button
          component={RouterLink}
          to="/farm-stay/book"
          size="large"
          endIcon={<ArrowForwardIcon />}
          sx={{
            mt: 5, bgcolor: brand.mustard, backgroundImage: 'none', color: ink.onMustard,
            fontWeight: 700, px: 5, py: 1.7, borderRadius: 0, fontSize: 16,
            '&:hover': { bgcolor: '#C2A20E', backgroundImage: 'none' },
          }}
        >
          Check dates
        </Button>
      </Reveal>
    </Container>
  </Box>
);

const FarmStayPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pkg, setPkg] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.graphql({ query: getStayPackageBySlug, variables: { slug: PACKAGE_SLUG } });
      setPkg(res.data?.listStayPackages?.items?.[0] || null);
      try {
        const ph = await client.graphql({ query: listFarmPhotos });
        setPhotos(
          (ph.data?.listGalleryImages?.items || [])
            .filter((p) => p?.originalUrl)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((p) => p.originalUrl),
        );
      } catch {
        // photography is decorative here; the page reads fine on the palette alone
        setPhotos([]);
      }
    } catch (e) {
      console.error('Error loading farm stay:', e);
      setError('We could not load the farm stay details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const days = useMemo(
    () => [...(pkg?.itineraryDays?.items || [])].sort((a, b) => a.dayIndex - b.dayIndex),
    [pkg],
  );

  if (loading) {
    // Reserve the hero and the first section, so the footer does not travel
    // when the data lands. See PR #31.
    return (
      <Box sx={{ bgcolor: surface.page }}>
        <Skeleton variant="rectangular" sx={{ height: { xs: '86vh', md: '94vh' }, bgcolor: surface.dark, opacity: 0.4 }} />
        <Container maxWidth="lg" sx={{ py: { xs: 9, md: 16 } }}>
          <Skeleton height={20} width={160} />
          <Skeleton height={54} width="70%" sx={{ mt: 2 }} />
          <Skeleton height={24} sx={{ mt: 3 }} />
          <Skeleton height={24} width="85%" />
          <Skeleton height={24} width="60%" />
          <Skeleton variant="rectangular" height={420} sx={{ mt: 8 }} />
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 12 }}>
        <Alert severity="error" action={<Button onClick={load} size="small">Try again</Button>}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!pkg) {
    return (
      <Container maxWidth="md" sx={{ py: 14, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ ...DISPLAY, color: ink.primary }}>
          The farm stay is being written up
        </Typography>
        <Typography sx={{ color: ink.secondary, mt: 2 }}>
          Get in touch and we will tell you all about a weekend at Vidhi.
        </Typography>
        <Button
          component={RouterLink}
          to="/contact"
          sx={{ mt: 4, bgcolor: brand.mustard, backgroundImage: 'none', color: ink.onMustard, px: 4, borderRadius: 0 }}
        >
          Contact us
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: surface.page }}>
      <Seo
        title="Weekend Farm Stay near Shantiniketan"
        description={
          pkg.price != null
            ? `Two nights on an organic farm in Birbhum, near Shantiniketan. Bengali home cooking, a Baul music evening and a bonfire, at ${formatINR(pkg.price)} per person per night.`
            : 'Two nights on an organic farm in Birbhum, near Shantiniketan. Bengali home cooking, a Baul music evening and a bonfire.'
        }
        path="/farm-stay"
        image={photos[0] || pkg.heroImage || undefined}
        jsonLd={stayJsonLd(pkg)}
      />

      <Hero pkg={pkg} photo={photos[0] || pkg.heroImage} isMobile={isMobile} />
      <Preamble />
      {days.length > 0 && <Days days={days} photos={photos.slice(1)} />}
      <Table pkg={pkg} />
      <BaulEvening photo={photos[2] || photos[0]} />
      <Cost pkg={pkg} />
      <Practicalities pkg={pkg} />
      <TakeHome />
      <Closing pkg={pkg} />

      <StickyBookBar pkg={pkg} />
      {/* the sticky bar sits over the footer otherwise */}
      <Box sx={{ height: { xs: 76, md: 84 } }} />
    </Box>
  );
};

export default FarmStayPage;
